import React, { useState, useEffect } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import {
  ClipboardList,
  DollarSign,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Calculator,
  TrendingUp,
  TrendingDown,
  Package,
  CheckCircle2,
  AlertCircle,
  Info,
  Building2,
  Users,
  CalendarRange,
  BarChart3,
  TrendingUp as TrendIcon,
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { formatDateForInput, createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import type { DateRange } from '@/types'

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface TransferItemLine {
  name: string
  quantity: number
  unit: string
  price: number
}

interface IndentItem {
  itemName: string
  totalQuantity: number
  unit: string
  totalValue: number
  transferCount: number
  trend: 'up' | 'down' | 'stable'
  departments: string[]
}

interface DeptCost {
  department: string
  totalCost: number
  indentCount: number
  items: string[]
}

interface DeptActivity {
  fromDepartment: string
  toDepartment: string
  transferCount: number
  totalValue: number
  pendingCount: number
  completedCount: number
}

interface UserWise {
  userName: string
  userEmail: string
  indentCount: number
  totalValue: number
  pendingCount: number
  completedCount: number
}

interface TrendEntry {
  date: string
  indentCount: number
  totalValue: number
}

interface RecentTransfer {
  transferId: string
  fromDepartment: string
  toDepartment: string
  status: string
  priority: string
  requestedBy: string
  itemCount: number
  totalValue: number
  createdAt: string
  pendingDays?: number
  items: TransferItemLine[]
}

interface IndentReportData {
  summary: {
    totalIndents: number
    totalIndentValue: number
    avgPendingDays: number
    pendingIndents: number
    completedIndents: number
  }
  itemConsumption: IndentItem[]
  deptCost: DeptCost[]
  deptActivity: DeptActivity[]
  userWise: UserWise[]
  trends: TrendEntry[]
  recentTransfers: RecentTransfer[]
  alerts: Array<{ type: 'error' | 'warning' | 'info'; message: string }>
  recommendations: string[]
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDays(days: number): string {
  if (days === 0) return '0 days'
  if (days < 0.1) return `${days.toFixed(2)} days`
  if (days < 1)   return `${days.toFixed(1)} days`
  return `${days.toFixed(1)} days`
}

function groupTrends(entries: TrendEntry[], period: 'daily' | 'weekly' | 'monthly') {
  if (!entries.length) return []
  if (period === 'daily') {
    return entries.map(e => ({
      label: new Date(e.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      indentCount: e.indentCount,
      totalValue: e.totalValue,
    }))
  }
  const buckets = new Map<string, { label: string; indentCount: number; totalValue: number }>()
  entries.forEach((e, i) => {
    const key = period === 'weekly'
      ? `Week ${Math.floor(i / 7) + 1}`
      : new Date(e.date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
    if (!buckets.has(key)) buckets.set(key, { label: key, indentCount: 0, totalValue: 0 })
    const b = buckets.get(key)!
    b.indentCount += e.indentCount
    b.totalValue  += e.totalValue
  })
  return Array.from(buckets.values())
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  return (
    <div className="bg-background border rounded-xl shadow-md p-3 text-sm space-y-1">
      <p className="font-medium">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {p.name === 'Total Value' ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

// ─── Dept Detail Modal ───────────────────────────────────────────────────────

interface DeptDetailModalProps {
  dept: DeptCost
  transfers: RecentTransfer[]  // already filtered for this dept
  open: boolean
  onClose: () => void
}

function DeptDetailModal({ dept, transfers, open, onClose }: DeptDetailModalProps) {
  const { formatCurrency, currencySymbol } = useCurrency()
  // ── Derived data ──────────────────────────────────────────────
  const avgValue = dept.indentCount > 0 ? dept.totalCost / dept.indentCount : 0

  // Item Consumption
  const itemMap = new Map<string, { name: string; qty: number; unit: string; value: number }>()
  transfers.forEach(t => {
    (t.items || []).forEach(item => {
      if (!itemMap.has(item.name)) itemMap.set(item.name, { name: item.name, qty: 0, unit: item.unit, value: 0 })
      const e = itemMap.get(item.name)!
      e.qty   += item.quantity
      e.value += item.price * item.quantity
    })
  })
  const totalQty = Array.from(itemMap.values()).reduce((s, i) => s + i.qty, 0)
  const itemConsumption = Array.from(itemMap.values())
    .map(i => ({ ...i, pct: totalQty > 0 ? (i.qty / totalQty) * 100 : 0 }))
    .sort((a, b) => b.qty - a.qty)

  // Indent Summary (flat per-item per-transfer rows)
  const indentSummaryRows: { date: string; item: string; qty: number; unit: string }[] = []
  transfers.forEach(t => {
    (t.items || []).forEach(item => {
      indentSummaryRows.push({
        date: t.createdAt,
        item: item.name,
        qty: item.quantity,
        unit: item.unit,
      })
    })
  })
  indentSummaryRows.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  // User Wise
  const userMap = new Map<string, { name: string; count: number; totalValue: number }>()
  transfers.forEach(t => {
    const key = t.requestedBy || 'Unknown'
    if (!userMap.has(key)) userMap.set(key, { name: key, count: 0, totalValue: 0 })
    const e = userMap.get(key)!
    e.count++
    e.totalValue += t.totalValue
  })
  const userWise = Array.from(userMap.values())
    .map(u => ({ ...u, avgValue: u.count > 0 ? u.totalValue / u.count : 0 }))
    .sort((a, b) => b.count - a.count)

  // Trends — group transfers into up to 4 weeks
  const weekBuckets = new Map<string, { count: number; value: number }>()
  if (transfers.length > 0) {
    const earliest = Math.min(...transfers.map(t => new Date(t.createdAt).getTime()))
    transfers.forEach(t => {
      const ms  = new Date(t.createdAt).getTime()
      const wk  = Math.floor((ms - earliest) / (7 * 86400000)) + 1
      const key = `Week ${wk}`
      if (!weekBuckets.has(key)) weekBuckets.set(key, { count: 0, value: 0 })
      const e = weekBuckets.get(key)!
      e.count++
      e.value += t.totalValue
    })
  }
  const weekData = Array.from(weekBuckets.entries())
    .map(([label, d]) => ({ label, count: d.count, value: d.value }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const maxCount = Math.max(...weekData.map(w => w.count), 1)
  const peakWeek = weekData.reduce((best, w) => w.count > best.count ? w : best, weekData[0] ?? { label: '-', count: 0, value: 0 })

  // Chart data for the trend mini-chart
  const trendChartData = weekData.map(w => ({ name: w.label, value: w.value }))

  // ── Render ────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent className="max-w-2xl w-full gap-0 p-0">
        <div className="p-6 pb-0">
          {/* Badge + close */}
          <div className="flex items-start justify-between mb-3">
            <Badge variant="secondary" className="text-xs px-2 py-1 gap-1.5">
              <Building2 className="h-3 w-3" />
              {dept.department} Department
            </Badge>
          </div>

          {/* Title */}
          <DialogHeader className="mb-4">
            <DialogTitle className="text-2xl font-bold capitalize">{dept.department}</DialogTitle>
            <DialogDescription>Detailed indent report</DialogDescription>
          </DialogHeader>

          {/* Summary stat cards */}
          <div className="grid grid-cols-3 gap-0 border rounded-xl overflow-hidden mb-5">
            <div className="p-4 border-r">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Total Cost</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(dept.totalCost)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{dept.indentCount} indent{dept.indentCount !== 1 ? 's' : ''}</p>
              <p className="text-xs text-green-600 font-medium mt-1">This period</p>
            </div>
            <div className="p-4 border-r">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Total Indents</p>
              <p className="text-2xl font-bold mt-1">{dept.indentCount}</p>
              <p className="text-xs text-muted-foreground mt-0.5">This period</p>
              <p className="text-xs text-green-600 font-medium mt-1">
                {transfers.length > 0 ? `${transfers.length} transfer${transfers.length !== 1 ? 's' : ''}` : 'No transfers'}
              </p>
            </div>
            <div className="p-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Avg. Value</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(avgValue)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Per indent</p>
              <p className="text-xs text-muted-foreground mt-1">No change</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="item-consumption" className="w-full">
          <div className="px-6 border-b">
            <TabsList className="h-auto bg-transparent p-0 gap-0 w-full justify-start rounded-none">
              {[
                { value: 'item-consumption', label: 'Item Consumption' },
                { value: 'indent-summary',   label: 'Indent Summary' },
                { value: 'user-wise',        label: 'User Wise' },
                { value: 'trends',           label: 'Trends' },
              ].map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-muted-foreground data-[state=active]:text-primary transition-colors"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* ── Item Consumption ── */}
          <TabsContent value="item-consumption" className="mt-0">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-4 w-4 text-green-600" />
                <p className="text-sm font-semibold">Item consumption breakdown</p>
              </div>
              {itemConsumption.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No item data available.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Item</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Quantity</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Unit</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {itemConsumption.map(item => (
                      <tr key={item.name} className="hover:bg-muted/20">
                        <td className="px-3 py-3 font-medium">{item.name}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-24 bg-muted rounded-full h-1.5 flex-shrink-0">
                              <div
                                className="bg-primary h-1.5 rounded-full"
                                style={{ width: `${item.pct}%` }}
                              />
                            </div>
                            <span>{item.qty % 1 === 0 ? item.qty : item.qty.toFixed(2)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">{item.unit}</td>
                        <td className="px-3 py-3 text-right font-medium">{item.pct.toFixed(0)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* ── Indent Summary ── */}
          <TabsContent value="indent-summary" className="mt-0">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="h-4 w-4 text-blue-600" />
                <p className="text-sm font-semibold">Indent summary</p>
              </div>
              {indentSummaryRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No transfers found.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Date</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Item</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">Qty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {indentSummaryRows.map((row, i) => (
                        <tr key={i} className="hover:bg-muted/20">
                          <td className="px-3 py-3 text-muted-foreground">
                            {new Date(row.date).toLocaleDateString('en-CA')}
                          </td>
                          <td className="px-3 py-3 font-medium">{row.item}</td>
                          <td className="px-3 py-3">{row.qty} {row.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── User Wise ── */}
          <TabsContent value="user-wise" className="mt-0">
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="h-4 w-4 text-orange-500" />
                <p className="text-sm font-semibold">User-wise activity</p>
              </div>
              {userWise.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No user data available.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase">User</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase">Indents</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase">Total Value</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-muted-foreground uppercase">Avg. Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {userWise.map(u => (
                      <tr key={u.name} className="hover:bg-muted/20">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium">{u.count}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(u.totalValue)}</td>
                        <td className="px-3 py-3 text-right text-muted-foreground">{formatCurrency(u.avgValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          {/* ── Trends ── */}
          <TabsContent value="trends" className="mt-0">
            <div className="p-6 space-y-5">
              {/* Horizontal week bars */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <TrendIcon className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold">
                    {weekData.length <= 4 ? 'Weekly' : 'Period'} indent trend
                  </p>
                </div>
                {weekData.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No trend data.</p>
                ) : (
                  <div className="space-y-2.5">
                    {weekData.map(w => {
                      const pct = maxCount > 0 ? (w.count / maxCount) * 100 : 0
                      return (
                        <div key={w.label} className="flex items-center gap-3 text-sm">
                          <span className="w-14 text-xs text-muted-foreground flex-shrink-0">{w.label}</span>
                          <div className="flex-1 bg-muted rounded-full h-7 relative overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary transition-all flex items-center"
                              style={{ width: pct > 0 ? `max(${pct}%, 32px)` : '32px' }}
                            >
                              <span className="text-xs font-bold text-primary-foreground px-2">{w.count}</span>
                            </div>
                          </div>
                          <span className="w-16 text-right text-xs text-muted-foreground flex-shrink-0">
                            {formatCurrency(w.value)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Peak + This month cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-xl p-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Peak Week</p>
                  <p className="text-xl font-bold mt-1">{peakWeek?.label ?? '-'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {peakWeek?.count ?? 0} indent{(peakWeek?.count ?? 0) !== 1 ? 's' : ''} · {formatCurrency(peakWeek?.value ?? 0)}
                  </p>
                </div>
                <div className="border rounded-xl p-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">This Month</p>
                  <p className="text-xl font-bold mt-1">{dept.indentCount} indent{dept.indentCount !== 1 ? 's' : ''}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatCurrency(dept.totalCost)} total</p>
                </div>
              </div>

              {/* Mini bar chart */}
              {trendChartData.length > 0 && (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendChartData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={v => `${currencySymbol}${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null
                        return (
                          <div className="bg-background border rounded-lg shadow p-2 text-xs">
                            <p className="font-medium">{label}</p>
                            <p className="text-green-600">{formatCurrency(payload[0].value as number)}</p>
                          </div>
                        )
                      }} />
                      <Bar dataKey="value" name="Value" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

// ─── Main Screen ─────────────────────────────────────────────────────────────

export default function IndentReportScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [reportData, setReportData] = useState<IndentReportData | null>(null)
  const [isLoading, setIsLoading]   = useState(true)
  const [error, setError]           = useState<string | null>(null)
  const [dateRange, setDateRange]   = useBusinessDayDefaultRange()
  const [period, setPeriod]         = useState<'daily' | 'weekly' | 'monthly'>('weekly')
  const [outlet, setOutlet]         = useState<string>('all')
  const [modalDept, setModalDept]   = useState<DeptCost | null>(null)

  const loadReport = async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getIndentReport(currentRestaurant._id, dateRange, { period, outlet })
      if (response.success) {
        setReportData(response.data)
      } else {
        setError(response.message || 'Failed to load indent report')
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load indent report'
        : 'Failed to load indent report'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) loadReport()
  }, [currentRestaurant?._id])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    setDateRange(prev => {
      const d = parseDate(value)
      return field === 'from' ? createDateRange(d, prev.to) : createDateRange(prev.from, d)
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading indent report...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl sm:text-2xl font-bold">Indent Reports</h1>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-2 py-[10px]" onClick={loadReport}>
              <RefreshCw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const {
    summary,
    itemConsumption = [],
    deptCost        = [],
    deptActivity    = [],
    userWise        = [],
    trends          = [],
    recentTransfers = [],
    alerts          = [],
    recommendations = [],
  } = reportData || {}

  const chartData    = groupTrends(trends, period)
  const maxDeptCost  = Math.max(...deptCost.map(d => d.totalCost), 1)
  const avgPerPeriod = chartData.length > 0 ? (summary?.totalIndents ?? 0) / chartData.length : 0

  // Transfers for the selected modal dept
  const modalTransfers = modalDept
    ? recentTransfers.filter(t => t.fromDepartment === modalDept.department)
    : []

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Indent Reports</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Monitor indents, approvals, fulfillment, and department-wise spending.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-xs">
            <CalendarRange className="h-3.5 w-3.5" />
            Live reporting snapshot
          </Badge>
          <Button onClick={loadReport} variant="outline" size="sm" disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Reporting Basics ──────────────────────────────────── */}
      <Card>
        <CardContent className="pt-4 pb-3">
          {/* Single filter row — wraps gracefully on mobile */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Period toggle */}
            <div className="flex rounded-md border overflow-hidden h-8 flex-shrink-0">
              {(['daily', 'weekly', 'monthly'] as const).map((p, i) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={[
                    'px-3 text-xs font-medium capitalize transition-colors focus:outline-none',
                    i > 0 ? 'border-l' : '',
                    period === p
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-border flex-shrink-0" />

            {/* Outlet selector */}
            <select
              value={outlet}
              onChange={e => setOutlet(e.target.value)}
              className="h-8 px-2 pr-7 rounded-md border border-input bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring flex-shrink-0"
            >
              <option value="all">All Outlets</option>
            </select>

            {/* Divider */}
            <div className="hidden sm:block w-px h-5 bg-border flex-shrink-0" />

            {/* Date range picker */}
            <div className="flex-shrink-0">
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={handleDateChange}
                onApply={loadReport}
                title="Indent Report Period"
                description="Select the date range for your indent report"
              />
            </div>

            {/* Apply */}
            <Button onClick={loadReport} disabled={isLoading} size="sm" className="h-8 flex-shrink-0">
              {isLoading
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                : <Calculator className="h-3.5 w-3.5 mr-1.5" />}
              Apply
            </Button>

            {/* Status pill — pushed to end on larger screens */}
            <p className="text-xs text-muted-foreground sm:ml-auto">
              <span className="font-medium text-foreground capitalize">{period}</span>
              {' · '}
              <span className="font-medium text-foreground">{outlet === 'all' ? 'All Outlets' : outlet}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ── Summary Cards ───────────────────────────────────── */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Indents</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalIndents ?? 0}</div>
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="text-xs text-yellow-600">{summary?.pendingIndents ?? 0} pending</span>
              <span className="text-xs text-green-600">{summary?.completedIndents ?? 0} completed</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Indent Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary?.totalIndentValue ?? 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Across all departments</p>
          </CardContent>
        </Card>

        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Pending Days</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${(summary?.avgPendingDays ?? 0) > 3 ? 'text-orange-600' : ''}`}>
              {formatDays(summary?.avgPendingDays ?? 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Time to approval</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Department Spend Table ──────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Department Spend
          </CardTitle>
          <CardDescription>Total indent value by department — click View Details for a breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          {deptCost.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No department data for this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[360px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Total Spend</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Indents</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deptCost.map(dept => (
                    <tr key={dept.department} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium capitalize">{dept.department}</td>
                      <td className="px-4 py-3">{formatCurrency(dept.totalCost)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{dept.indentCount}</td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-primary hover:text-primary hover:bg-primary/10"
                          onClick={() => setModalDept(dept)}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Date-wise Trend Chart ──────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Date-wise Trend Report
          </CardTitle>
          <CardDescription>Indent count and value over the selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No trend data available.</p>
          ) : (
            <>
              <div className="h-72 sm:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={{ stroke: 'hsl(var(--muted-foreground))' }}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="count"
                      orientation="left"
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      yAxisId="value"
                      orientation="right"
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `${currencySymbol}${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                    <Bar yAxisId="count" dataKey="indentCount" name="Indent Count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    <Bar yAxisId="value"  dataKey="totalValue"  name="Total Value"  fill="#10b981"              radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-4">
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Total Indents</p>
                  <p className="text-xl font-bold mt-1">{summary?.totalIndents ?? 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Current period</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Total Value</p>
                  <p className="text-xl font-bold mt-1">{formatCurrency(summary?.totalIndentValue ?? 0)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Current period</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">
                    Avg./{period === 'daily' ? 'Day' : period === 'weekly' ? 'Week' : 'Month'}
                  </p>
                  <p className="text-xl font-bold mt-1">{avgPerPeriod.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Per {period}</p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Item Consumption ──────────────────────────────── */}
      {itemConsumption.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5" />Item Consumption</CardTitle>
            <CardDescription>{itemConsumption.length} unique item{itemConsumption.length !== 1 ? 's' : ''} transferred</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">Item</th>
                    <th className="text-right py-3 px-2 font-medium">Qty</th>
                    <th className="text-left py-3 px-2 font-medium">Unit</th>
                    <th className="text-right py-3 px-2 font-medium">Value</th>
                    <th className="text-right py-3 px-2 font-medium">Transfers</th>
                    <th className="text-left py-3 px-2 font-medium">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {itemConsumption.map(item => (
                    <tr key={item.itemName} className="border-b hover:bg-muted/30">
                      <td className="py-3 px-2 font-medium">{item.itemName}</td>
                      <td className="py-3 px-2 text-right">{item.totalQuantity.toFixed(2)}</td>
                      <td className="py-3 px-2 text-muted-foreground">{item.unit}</td>
                      <td className="py-3 px-2 text-right">{formatCurrency(item.totalValue)}</td>
                      <td className="py-3 px-2 text-right text-muted-foreground">{item.transferCount}</td>
                      <td className="py-3 px-2">
                        {item.trend === 'up'     && <TrendingUp   className="h-4 w-4 text-green-500" />}
                        {item.trend === 'down'   && <TrendingDown className="h-4 w-4 text-red-500" />}
                        {item.trend === 'stable' && <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lg:hidden space-y-2">
              {itemConsumption.map(item => (
                <div key={item.itemName} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{item.itemName}</span>
                    {item.trend === 'up'   && <TrendingUp   className="h-4 w-4 text-green-500" />}
                    {item.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Qty: </span>{item.totalQuantity.toFixed(2)} {item.unit}</div>
                    <div><span className="text-muted-foreground">Value: </span>{formatCurrency(item.totalValue)}</div>
                    <div><span className="text-muted-foreground">Transfers: </span>{item.transferCount}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Dept Activity ───────────────────────────────────── */}
      {deptActivity.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Department Activity</CardTitle>
            <CardDescription>Transfer activity between departments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">From</th>
                    <th className="text-left py-3 px-2 font-medium">To</th>
                    <th className="text-right py-3 px-2 font-medium">Transfers</th>
                    <th className="text-right py-3 px-2 font-medium">Value</th>
                    <th className="text-right py-3 px-2 font-medium">Pending</th>
                    <th className="text-right py-3 px-2 font-medium">Done</th>
                  </tr>
                </thead>
                <tbody>
                  {deptActivity.map((act, i) => (
                    <tr key={i} className="border-b hover:bg-muted/30">
                      <td className="py-3 px-2 capitalize">{act.fromDepartment}</td>
                      <td className="py-3 px-2 capitalize">{act.toDepartment}</td>
                      <td className="py-3 px-2 text-right font-medium">{act.transferCount}</td>
                      <td className="py-3 px-2 text-right">{formatCurrency(act.totalValue)}</td>
                      <td className="py-3 px-2 text-right">
                        {act.pendingCount > 0
                          ? <span className="text-yellow-600 font-medium">{act.pendingCount}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-green-600 font-medium">{act.completedCount}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── User Wise ───────────────────────────────────────── */}
      {userWise.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />User Wise Report</CardTitle>
            <CardDescription>Indent activity per staff member</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">User</th>
                    <th className="text-left py-3 px-2 font-medium hidden sm:table-cell">Email</th>
                    <th className="text-right py-3 px-2 font-medium">Indents</th>
                    <th className="text-right py-3 px-2 font-medium">Value</th>
                    <th className="text-right py-3 px-2 font-medium">Pending</th>
                    <th className="text-right py-3 px-2 font-medium">Done</th>
                  </tr>
                </thead>
                <tbody>
                  {userWise.map(u => (
                    <tr key={u.userEmail} className="border-b hover:bg-muted/30">
                      <td className="py-3 px-2 font-medium">{u.userName}</td>
                      <td className="py-3 px-2 text-muted-foreground text-xs hidden sm:table-cell">{u.userEmail}</td>
                      <td className="py-3 px-2 text-right font-medium">{u.indentCount}</td>
                      <td className="py-3 px-2 text-right">{formatCurrency(u.totalValue)}</td>
                      <td className="py-3 px-2 text-right">
                        {u.pendingCount > 0
                          ? <span className="text-yellow-600 font-medium">{u.pendingCount}</span>
                          : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-green-600 font-medium">{u.completedCount}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── All Indents ─────────────────────────────────────── */}
      {recentTransfers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />All Indents</CardTitle>
            <CardDescription>{recentTransfers.length} transfers in selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">ID</th>
                    <th className="text-left py-3 px-2 font-medium">From → To</th>
                    <th className="text-left py-3 px-2 font-medium">Requested By</th>
                    <th className="text-right py-3 px-2 font-medium">Items</th>
                    <th className="text-right py-3 px-2 font-medium">Value</th>
                    <th className="text-center py-3 px-2 font-medium">Status</th>
                    <th className="text-right py-3 px-2 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTransfers.map(t => (
                    <tr key={t.transferId} className="border-b hover:bg-muted/30">
                      <td className="py-3 px-2 font-mono text-xs text-muted-foreground">{t.transferId.slice(-8)}</td>
                      <td className="py-3 px-2 text-xs capitalize">{t.fromDepartment} → {t.toDepartment}</td>
                      <td className="py-3 px-2">{t.requestedBy}</td>
                      <td className="py-3 px-2 text-right">{t.itemCount}</td>
                      <td className="py-3 px-2 text-right">{formatCurrency(t.totalValue)}</td>
                      <td className="py-3 px-2 text-center">
                        <Badge
                          variant={t.status === 'completed' ? 'default' : t.status === 'cancelled' ? 'destructive' : 'secondary'}
                          className="text-xs capitalize"
                        >
                          {t.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-right text-xs text-muted-foreground">
                        {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Alerts & Recommendations ────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Active Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                <span className="text-sm">All clear — no active alerts</span>
              </div>
            ) : (
              <div className="space-y-2">
                {alerts.map((alert, i) => (
                  <Alert
                    key={i}
                    variant={alert.type === 'error' ? 'destructive' : 'default'}
                    className={
                      alert.type === 'warning' ? 'border-yellow-500/50 bg-yellow-50/50 dark:bg-yellow-950/20' :
                      alert.type === 'info'    ? 'border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/20' : ''
                    }
                  >
                    {alert.type === 'error'   && <AlertTriangle className="h-4 w-4" />}
                    {alert.type === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                    {alert.type === 'info'    && <Info className="h-4 w-4 text-blue-600" />}
                    <AlertDescription className={
                      alert.type === 'warning' ? 'text-yellow-800 dark:text-yellow-300' :
                      alert.type === 'info'    ? 'text-blue-800 dark:text-blue-300' : ''
                    }>
                      {alert.message}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recommendations.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recommendations at this time.</p>
            ) : (
              <ul className="space-y-2">
                {recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Mobile Floating Filter ──────────────────────────── */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadReport}
        title="Indent Report Date Range"
        description="Select the date range for your indent report"
      />

      {/* ── Dept Detail Modal ───────────────────────────────── */}
      {modalDept && (
        <DeptDetailModal
          dept={modalDept}
          transfers={modalTransfers}
          open={!!modalDept}
          onClose={() => setModalDept(null)}
        />
      )}
    </div>
  )
}
